import type { SelectProps, ThemeConfig } from 'antd'
import { ConfigProvider, Flex, Select } from 'antd'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useLocalStorage } from 'react-use'
import styled, { useTheme } from 'styled-components'

import { TicketFilterBar } from './components/filters/TicketFilterBar'
import { VirtualTicketList } from './components/VirtualTicketList'
import { useTicketFilters } from './filtering/useTicketFilters'
import { TicketBoardView } from './TicketBoardView'
import { TicketModal } from './TicketModal'
import {
  type BoardConfig,
  type CardView,
  LIST_VIEW_ID,
  type TicketDashboardConfig,
} from './types'

const ViewSelect = styled(Select<string>)`
  flex-shrink: 0;
  min-width: 11rem;
`

/**
 * Takes the space left in its column-flex parent, so the list or board scrolls
 * on its own instead of the page. The minimum keeps it usable on short screens.
 */
const Root = styled(Flex)`
  flex: 1 0 0;
  min-height: 30rem;
`

function getViewOptions(
  boards: ReadonlyArray<BoardConfig>
): SelectProps['options'] {
  return [
    { label: 'List', value: LIST_VIEW_ID },
    ...boards.map((board) => ({ label: board.label, value: board.id })),
  ]
}

interface TicketDashboardViewProps {
  /** Keep stable (module constant or memoised); every derived memo depends on it. */
  config: TicketDashboardConfig
  cards: ReadonlyArray<CardView>
  onCreateVersion?: (ticketId: string) => void
}

export const TicketDashboardView: FC<TicketDashboardViewProps> = ({
  config,
  cards,
  onCreateVersion,
}) => {
  const { categories, boards } = config
  const [viewId, setViewId] = useLocalStorage(
    `ticket-dashboard-view:${config.id}`,
    LIST_VIEW_ID,
    { raw: true }
  )
  const [selectedCardId, setSelectedCardId] = useState<string>()
  const { state, filters, visibleRows, setQuery, setCategoryValues } =
    useTicketFilters(cards, categories)

  const visibleCards = useMemo(
    () => visibleRows.map((row) => row.card),
    [visibleRows]
  )
  const activeBoard = useMemo(
    () => boards.find((board) => board.id === viewId),
    [boards, viewId]
  )
  const viewOptions = useMemo(() => getViewOptions(boards), [boards])
  const selectedCard = useMemo(
    () => cards.find((card) => card.id === selectedCardId),
    [cards, selectedCardId]
  )
  const handleCloseModal = useCallback(() => setSelectedCardId(undefined), [])

  const theme = useTheme()
  /** Matches the legacy views: their grey cards and columns, and fully rounded pills. */
  const cardTheme = useMemo<ThemeConfig>(
    () => ({
      components: {
        Card: {
          colorBgContainer: theme.colors.gray[800],
          colorBorderSecondary: theme.colors.gray[700],
        },
        Tag: { borderRadiusSM: 999 },
      },
    }),
    [theme.colors.gray]
  )

  return (
    <ConfigProvider theme={cardTheme} tag={{ variant: 'outlined' }}>
      <Root vertical gap='middle'>
        <TicketFilterBar
          filters={filters}
          selection={state.selection}
          query={state.query}
          onQueryChange={setQuery}
          onCategoryChange={setCategoryValues}
          leading={
            boards.length > 0 && (
              <ViewSelect
                prefix='View'
                value={activeBoard?.id ?? LIST_VIEW_ID}
                options={viewOptions}
                onChange={setViewId}
                popupMatchSelectWidth={false}
              />
            )
          }
        />

        {activeBoard ? (
          <TicketBoardView
            board={activeBoard}
            rows={visibleRows}
            categories={categories}
            onSelect={setSelectedCardId}
          />
        ) : (
          <VirtualTicketList
            cards={visibleCards}
            categories={categories}
            onSelect={setSelectedCardId}
          />
        )}

        {selectedCard && (
          <TicketModal
            card={selectedCard}
            categories={categories}
            onClose={handleCloseModal}
            onCreateVersion={onCreateVersion}
          />
        )}
      </Root>
    </ConfigProvider>
  )
}
